import React, { useEffect, useRef, useState } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { lightColours } from '../styles/colours';

export default function LeadForm({ title = "Stay Updated", subtitle = "Get notified about new features and updates" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);

  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    // Skip Turnstile in development mode to avoid domain validation errors
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    if (!TURNSTILE_SITE_KEY) {
      return;
    }

    const renderTurnstile = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current) return;
      
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => setToken(token),
          "expired-callback": () => setToken(""),
          "error-callback": () => {
            setToken("");
          },
        });
      } catch (error) {
        // Silently handle render errors
      }
    };

    // If Turnstile is already loaded, render immediately
    if (window.turnstile) {
      renderTurnstile();
    } else {
      // Wait for Turnstile to load
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          renderTurnstile();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkTurnstile), 10000);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          // Silently handle cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [TURNSTILE_SITE_KEY]);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    setMsg("");

    if (!email.trim()) {
      setStatus("error");
      setMsg("Please enter your email address.");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }

    if (!gdprConsent) {
      setStatus("error");
      setMsg("Please confirm that you consent to receiving emails from us.");
      return;
    }

    const form = new FormData(e.target);
    const payload = {
      email: email,
      turnstileToken: token,
      website: form.get("website") || "",
      gdprConsent: gdprConsent,
      consentTimestamp: new Date().toISOString()
    };

    if (TURNSTILE_SITE_KEY && process.env.NODE_ENV !== 'development' && !payload.turnstileToken) {
      setStatus("error");
      setMsg("Please complete the verification.");
      return;
    }

    try {
      // Development mode simulation
      if (process.env.NODE_ENV === 'development') {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Simulate success response for development
        setStatus("success");
        setMsg("✅ Development Mode: Form submission successful! (This would normally send to Mailchimp)");
        setEmail("");
        setGdprConsent(false);
        return;
      }

      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus("success");
        setMsg(data.message || "Thanks! Check your inbox to confirm your subscription.");
        setEmail("");
        setGdprConsent(false);
      } else {
        setStatus("error");
        setMsg(data.message || "Something went wrong — please try again.");
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        setStatus("success");
        setMsg("✅ Development Mode: Form would be submitted in production");
        setEmail("");
        setGdprConsent(false);
      } else {
        setStatus("error");
        setMsg("Network error — please check your connection and try again.");
      }
    }

    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        // Silently handle reset errors
      }
    }
  }

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3, 
        maxWidth: 400, 
        mx: 'auto',
        backgroundColor: lightColours.background
      }}
    >
      <Typography variant="h6" component="h2" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
      
      <Box component="form" onSubmit={onSubmit} noValidate>
        <TextField
          fullWidth
          type="email"
          label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          autoComplete="email"
          sx={{ mb: 2 }}
          error={status === "error" && email.trim() === ""}
        />

        <input 
          name="website" 
          tabIndex="-1" 
          autoComplete="off" 
          style={{ display: "none" }} 
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I consent to receiving emails from you and understand that I can unsubscribe at any time. 
              View our{' '}
              <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                privacy policy
              </Link>
              .
            </Typography>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        {TURNSTILE_SITE_KEY && process.env.NODE_ENV !== 'development' && (
          <Box sx={{ mb: 2 }}>
            <div ref={containerRef} className="cf-turnstile" />
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={status === "loading"}
          sx={{ 
            mb: 2,
            backgroundColor: lightColours.primary,
            '&:hover': {
              backgroundColor: lightColours.primaryDark
            }
          }}
        >
          {status === "loading" ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Subscribing...
            </>
          ) : (
            "Subscribe"
          )}
        </Button>

        {status === "success" && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {msg}
          </Alert>
        )}
        
        {status === "error" && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {msg}
          </Alert>
        )}
      </Box>
    </Paper>
  );
}
